import React from 'react';
import { Link } from 'react-router-dom';

const QuenchPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Quench by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 18 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Quench, a water intake tracking app developed by Wolf Technologies Ltd.
              This Privacy Policy explains how we collect, use, store, and protect your personal
              data when you use the Quench app and related services.
            </p>
            <p>
              We are committed to protecting your privacy and handling your data transparently and
              responsibly.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>We collect only the data necessary to provide and improve the Quench service:</p>
            <ul>
              <li><strong>Email address</strong> — used for account registration, login, and account-related communications.</li>
              <li><strong>Water intake logs</strong> — the amounts and times of drinks you record in the app.</li>
              <li><strong>Body weight</strong> — used solely to calculate your personalised daily hydration goal.</li>
              <li><strong>Daily goals</strong> — your calculated or adjusted daily water intake target.</li>
              <li><strong>Streak data</strong> — consecutive days you have met your hydration goal, including streak freeze usage.</li>
              <li><strong>Subscription status</strong> — your current plan (Free, Plus, or Pro) and subscription state.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We do not collect any of the following:</p>
            <ul>
              <li><strong>Location data</strong></li>
              <li><strong>Photos or videos</strong></li>
              <li><strong>Contacts</strong></li>
              <li><strong>Health data</strong> beyond hydration and body weight</li>
              <li><strong>Browsing history</strong></li>
              <li><strong>Search history</strong></li>
              <li><strong>Financial information</strong> (payments are handled directly by Apple and Stripe)</li>
              <li><strong>Advertising identifiers</strong></li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>Your data is used exclusively to:</p>
            <ul>
              <li><strong>Provide the service</strong> — log your water intake, display your history, and sync across sessions.</li>
              <li><strong>Calculate hydration goals</strong> — use your body weight to determine a personalised daily target (33ml per kg).</li>
              <li><strong>Track streaks</strong> — maintain your consecutive-day streak and manage streak freezes.</li>
              <li><strong>Manage subscriptions</strong> — verify your subscription tier and unlock the appropriate features.</li>
            </ul>
            <p>
              We do not use your data for advertising, profiling, or any purpose unrelated to the
              Quench service.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <ul>
              <li><strong>Region</strong> — all data is stored on Amazon Web Services (AWS) in the eu-west-2 (London) region.</li>
              <li><strong>Encryption at rest</strong> — data stored in Amazon DynamoDB is encrypted at rest using AWS-managed encryption keys.</li>
              <li><strong>Encryption in transit</strong> — all data transmitted between the app and our servers is encrypted using HTTPS (TLS).</li>
              <li><strong>Access controls</strong> — access to production data is restricted to authorised personnel only.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>Quench uses the following third-party services to operate:</p>
            <ul>
              <li><strong>Amazon Cognito</strong> — user authentication and account management. Receives email address and user ID.</li>
              <li><strong>Amazon DynamoDB</strong> — secure data storage for water intake logs, weight, goals, and streaks.</li>
              <li><strong>Stripe</strong> — web payment processing for subscriptions. Receives email address for billing; card details are handled entirely by Stripe and never touch our servers.</li>
              <li><strong>Apple In-App Purchase</strong> — iOS payment processing for subscriptions. Receives transaction receipts; payment details are handled entirely by Apple.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell, rent, or share your personal data with third parties
              for marketing or advertising purposes.
            </p>
            <p>
              Your data is only shared with the third-party services listed above, strictly for the
              purposes of providing the Quench service (authentication, storage, and payment
              processing).
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>Retention</strong> — your data is retained for as long as your account remains active.</li>
              <li><strong>Deletion</strong> — you can delete your account and all associated data at any time from Settings {'>'} Delete Account within the app. Upon account deletion, all your personal data — including water intake logs, weight, goals, streak data, and subscription records — will be permanently removed from our systems.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK General Data Protection Regulation (UK GDPR), you have the following
              rights:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data (available via Settings {'>'} Delete Account).</li>
              <li><strong>Data portability</strong> — request your data in a structured, machine-readable format.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — object to processing of your data in certain circumstances.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this
              page with an updated "Last updated" date. If we make significant changes that affect
              how we handle your data, we will notify you through the app or by email.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default QuenchPrivacy;
